import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Chip, Stack, TextField, Typography } from '@mui/material';

import { BundleDefinitionSummaryModel, UserInterfaceClient } from '../../../api/api';
import { useApiClient, useToastService } from '@@common';
import { ConfirmDialog } from '@@components';
import { parseVersionTags } from '../Utils/versionTags';

type RestoreAppVersionDialogProps = {
	version: BundleDefinitionSummaryModel | null;
	changeSetId?: string;
	changeSetName?: string;
	onClose: () => void;
	onRestored?: () => void;
};

export default function RestoreAppVersionDialog({ version, changeSetId, changeSetName, onClose, onRestored }: RestoreAppVersionDialogProps) {
	const client = useApiClient(UserInterfaceClient);
	const queryClient = useQueryClient();
	const toastService = useToastService();
	const [pending, setPending] = useState(false);
	const pendingRef = useRef(false);
	const [tagsText, setTagsText] = useState('');
	const restoreTag = version ? `restored-from:v${version.versionId}:${version.id}` : '';

	useEffect(() => {
		setTagsText(version?.tags?.filter((tag) => !tag.startsWith('restored-from:')).join('\n') ?? '');
	}, [version]);

	const handleRestore = async () => {
		if (!version?.definitionId || !changeSetId || pendingRef.current) {
			return;
		}

		pendingRef.current = true;
		setPending(true);
		try {
			// Read the persisted snapshot, including its published React build reference.
			const snapshot = await client.getById(version.id);
			if (snapshot.definitionId !== version.definitionId) {
				throw new Error('The selected version belongs to a different app.');
			}
			await client.update(snapshot.id, {
				changeSetId,
				name: snapshot.name,
				definition: snapshot.definition,
				tags: [...parseVersionTags(tagsText).filter((tag) => !tag.startsWith('restored-from:')), `restored-from:v${snapshot.versionId}:${snapshot.id}`],
			});
			void queryClient.invalidateQueries({ queryKey: ['userinterface'] });
			toastService.success(`App version ${version.versionId} restored as latest.`);
			onClose();
			onRestored?.();
		} catch (error) {
			toastService.error(`Failed to restore app version: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
		} finally {
			pendingRef.current = false;
			setPending(false);
		}
	};

	return (
		<ConfirmDialog
			open={Boolean(version)}
			title="Restore App Version"
			message={version ? `Restore version ${version.versionId} of "${version.name}" as the latest in change set "${changeSetName || changeSetId || 'unavailable'}"? This creates a new version and preserves existing history. The saved definition and React build reference will be restored. The editable React source workspace will remain unchanged.` : ''}
			confirmText={pending ? 'Restoring…' : 'Restore as latest'}
			confirmDisabled={pending || !version?.definitionId || !changeSetId}
			cancelDisabled={pending}
			onConfirm={handleRestore}
			onCancel={() => {
				if (!pendingRef.current) {
					onClose();
				}
			}}
		>
			<Stack spacing={2} sx={{ mt: 2 }}>
				<TextField
					label="Version tags"
					value={tagsText}
					onChange={(event) => setTagsText(event.target.value)}
					multiline
					minRows={2}
					fullWidth
					disabled={pending}
					helperText="One tag per line. Tags are saved on the new version."
				/>
				<Typography variant="body2" color="text.secondary">Restore source tag (added automatically)</Typography>
				<Chip label={restoreTag} size="small" variant="outlined" sx={{ alignSelf: 'flex-start', maxWidth: '100%' }} />
			</Stack>
		</ConfirmDialog>
	);
}
